import { useEffect, useMemo, useState } from "react";
import { EventLayout } from "@/components/EventLayout";
import { Gate } from "@/components/FeatureGate";
import { useEvent } from "@/context/EventContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Check, ListChecks } from "lucide-react";

export default function Tasks() {
  const { eapi, event } = useEvent();
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => eapi.get("/tasks").then(({ data }) => setTasks(sortTasks(data)));
  useEffect(() => { load(); }, []);

  const done = tasks.filter((t) => t.done).length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const grouped = useMemo(() => {
    const shown = tasks.filter((t) => filter === "all" || (filter === "open" ? !t.done : t.done));
    const g = {};
    shown.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    return g;
  }, [tasks, filter]);

  const toggle = async (t) => {
    setTasks((ts) => ts.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
    await eapi.put(`/tasks/${t.id}`, { ...t, done: !t.done }); load();
  };
  const add = async () => {
    if (!newTitle.trim()) return;
    await eapi.post("/tasks", { title: newTitle.trim(), category: "Custom", done: false });
    setNewTitle(""); toast.success("Task added"); load();
  };
  const remove = async (id) => { await eapi.delete(`/tasks/${id}`); load(); };
  const generate = async () => {
    try { const { data } = await eapi.post("/tasks/generate"); toast.success(`${data.created} suggested tasks added`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <EventLayout>
      <Gate featureKey="tasks">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="overline">Checklist</p>
            <h1 className="font-serif text-4xl tracking-tight">{event?.template?.name ? `Your ${event.template.name} checklist` : "Your planning checklist"}</h1>
          </div>
          <button onClick={generate} data-testid="generate-tasks-btn"
            className="flex items-center gap-2 rounded-full border border-[#8C9A86] text-[#5f6b59] px-5 py-2.5 text-sm hover:bg-[#8C9A86]/10 transition-colors">
            <Sparkles size={15} /> Suggest tasks by date
          </button>
        </div>

        {/* progress */}
        <div className="bg-white border border-[#E8E6E1] rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="overline flex items-center gap-2"><ListChecks size={13} /> Progress</p>
            <span className="text-sm text-stone-500">{done} of {tasks.length} done</span>
          </div>
          <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full bg-[#8C9A86] transition-all duration-700" style={{ width: `${progress}%` }} data-testid="task-progress" />
          </div>
        </div>

        {/* add + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex-1 flex gap-2">
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Add your own task…" data-testid="new-task-input"
              className="flex-1 rounded-full border border-[#E8E6E1] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
            <button onClick={add} data-testid="add-task-btn" className="rounded-full bg-[#2C2A28] text-white px-4 hover:bg-[#8C9A86] transition-colors"><Plus size={16} /></button>
          </div>
          <div className="flex gap-1.5">
            {["all", "open", "done"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`task-filter-${f}`}
                className={`px-4 py-2 rounded-full text-xs capitalize transition-colors ${filter === f ? "bg-[#8C9A86] text-white" : "bg-white border border-[#E8E6E1] text-stone-500 hover:bg-stone-50"}`}>{f}</button>
            ))}
          </div>
        </div>

        {Object.keys(grouped).length === 0 ? (
          <div className="bg-white border border-dashed border-[#E8E6E1] rounded-lg p-12 text-center text-sm text-stone-400">
            No tasks yet. Try "Suggest tasks by date".
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat}>
                <p className="overline mb-2">{cat}</p>
                <div className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden">
                  {list.map((t) => (
                    <div key={t.id} data-testid={`task-row-${t.id}`}
                      className="flex items-center gap-3 px-4 py-3 border-b border-[#F0EEE9] last:border-0 hover:bg-[#FDFBF7] transition-colors group">
                      <button onClick={() => toggle(t)} data-testid={`toggle-task-${t.id}`}
                        className={`w-5 h-5 rounded-full border grid place-items-center transition-colors ${t.done ? "bg-[#8C9A86] border-[#8C9A86] text-white" : "border-stone-300 hover:border-[#8C9A86]"}`}>
                        {t.done && <Check size={12} />}
                      </button>
                      <span className={`flex-1 text-sm ${t.done ? "line-through text-stone-400" : "text-stone-700"}`}>{t.title}</span>
                      {t.due_date && <span className="text-xs text-stone-400">{formatDue(t.due_date)}</span>}
                      <button onClick={() => remove(t.id)} data-testid={`delete-task-${t.id}`}
                        className="p-1.5 text-stone-300 hover:text-[#C67A7A] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </Gate>
    </EventLayout>
  );
}

function sortTasks(list) {
  return [...list].sort((a, b) => {
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  });
}
function formatDue(d) {
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
}
